import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Car, Loader2, Pencil, Star, Trash2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PageLoading } from "../components/States";
import { api } from "../lib/api";
import { setPageTitle } from "../lib/format";

const EMPTY = { nickname: "", make: "", model: "", year: "", colour: "", plate: "", region: "" };

export function VehicleForm({ initial, onSaved, onCancel, busy, setBusy }) {
  const [f, setF] = useState(() => {
    const v = { ...EMPTY, ...(initial || {}) };
    return { ...v, year: v.year ?? "", colour: v.colour ?? "", region: v.region ?? "" };
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, year: f.year ? parseInt(f.year, 10) : null };
      let saved = initial || null;
      if (initial?.vehicle_id) await api.updateVehicle(initial.vehicle_id, body);
      else saved = await api.addVehicle(body);
      toast.success(initial ? "Vehicle updated" : "Vehicle added");
      onSaved(saved);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 gap-3" data-testid="vehicle-form">
      <input required value={f.nickname} onChange={set("nickname")} maxLength={40} placeholder="Nickname (e.g. Daily driver)" className="input-dark" data-testid="vehicle-nickname" aria-label="Nickname" />
      <input value={f.plate} onChange={set("plate")} maxLength={12} placeholder="License plate" className="input-dark uppercase" data-testid="vehicle-plate" aria-label="License plate" />
      <input required value={f.make} onChange={set("make")} maxLength={40} placeholder="Make (e.g. Toyota)" className="input-dark" data-testid="vehicle-make" aria-label="Make" />
      <input required value={f.model} onChange={set("model")} maxLength={40} placeholder="Model (e.g. Corolla)" className="input-dark" data-testid="vehicle-model" aria-label="Model" />
      <input value={f.year} onChange={set("year")} inputMode="numeric" maxLength={4} placeholder="Year (optional)" className="input-dark" data-testid="vehicle-year" aria-label="Year" />
      <input value={f.colour} onChange={set("colour")} maxLength={30} placeholder="Colour (optional)" className="input-dark" data-testid="vehicle-colour" aria-label="Colour" />
      <input value={f.region} onChange={set("region")} maxLength={40} placeholder="Province/State (optional)" className="input-dark sm:col-span-2" data-testid="vehicle-region" aria-label="Region" />
      <div className="sm:col-span-2 flex gap-2">
        <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="vehicle-save-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {initial ? "Save changes" : "Add vehicle"}
        </button>
        {onCancel && <button type="button" onClick={onCancel} className="btn-secondary text-sm" data-testid="vehicle-cancel-btn">Cancel</button>}
      </div>
    </form>
  );
}

export default function MyVehicles() {
  const [vehicles, setVehicles] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.myVehicles().then(setVehicles).catch((e) => toast.error(e.message));
  useEffect(() => { setPageTitle("My Vehicles"); load(); }, []);

  const setDefault = async (id) => {
    try { await api.defaultVehicle(id); toast.success("Default vehicle updated"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this vehicle? Past bookings keep their historical record.")) return;
    try { await api.deleteVehicle(id); toast.success("Vehicle removed"); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="vehicles-page">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Driver</p>
        <h1 className="type-display mt-3">My Vehicles</h1>
        <p className="mt-3 text-sm text-zinc-500 max-w-xl">Pick a vehicle when you book. Your plate is shared with the host only once a booking is confirmed — it's never public.</p>

        {vehicles === null ? <div className="mt-10"><PageLoading label="Loading vehicles…" /></div> : (
          <div className="mt-8 space-y-4">
            {vehicles.map((v) => (
              <div key={v.vehicle_id} className="card-dark p-5" data-testid={`vehicle-card-${v.vehicle_id}`}>
                {editing === v.vehicle_id ? (
                  <VehicleForm initial={v} busy={busy} setBusy={setBusy}
                               onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <Car className="w-5 h-5 text-[#FF3B45] mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                          {v.nickname}
                          {v.is_default && <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 border border-emerald-500/30 rounded-full px-2 py-0.5" data-testid={`vehicle-default-badge-${v.vehicle_id}`}>Default</span>}
                        </p>
                        <p className="text-xs text-zinc-400 mt-1">{[v.make, v.model, v.year, v.colour].filter(Boolean).join(" · ")}</p>
                        <p className="text-xs text-zinc-500 mt-0.5 font-mono">{v.plate}{v.region ? ` · ${v.region}` : ""}</p>
                      </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      {!v.is_default && (
                        <button onClick={() => setDefault(v.vehicle_id)} className="p-2 rounded-lg text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors" data-testid={`vehicle-default-btn-${v.vehicle_id}`} aria-label="Set as default"><Star className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => setEditing(v.vehicle_id)} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-[#27272A] transition-colors" data-testid={`vehicle-edit-btn-${v.vehicle_id}`} aria-label="Edit vehicle"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(v.vehicle_id)} className="p-2 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`vehicle-delete-btn-${v.vehicle_id}`} aria-label="Delete vehicle"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {vehicles.length === 0 && !adding && (
              <p className="text-sm text-zinc-500" data-testid="vehicles-empty">No vehicles yet — add one to speed up booking.</p>
            )}
            {adding ? (
              <div className="card-dark p-5">
                <VehicleForm busy={busy} setBusy={setBusy} onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
              </div>
            ) : (
              vehicles.length < 10 && (
                <button onClick={() => setAdding(true)} className="btn-secondary text-sm w-full" data-testid="vehicle-add-btn">+ Add vehicle</button>
              )
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
